import Icon from '@/components/common/icons/Icon';
import {
    Center,
    CenterProps,
    chakra,
    forwardRef,
    HTMLChakraProps,
} from '@chakra-ui/react';
import { Goal } from '@common/goals';
import { GoalType } from '@common/graphql/generated';
import {
    getGoalIconBaseParams,
    getGoalIconSizeProps,
    GOAL_ICON_VIEWBOX,
    GoalIconSize,
} from '@common/react/goals';
import { useCurrentRateInfo } from '@common/react/rates';
import { useCurrentUser } from '@common/react/user';
import { toPx } from '@common/util/styles';
import { ForwardedRef } from 'react';
import { configByGoalType } from './config';

export interface GoalIconProps extends CenterProps {
    goal: Goal;
    size?: GoalIconSize;
    inverted?: boolean;
    filled?: boolean;
    showProgress?: boolean;
}

/*
    This adapts Chakra's "CircularProgress" component for our use-case, since
    Chakra's implementation doesn't correctly render "thickness" and "size"
    props based on how they hardcode their circles' "r" attributes.
    Source: https://github.com/chakra-ui/chakra-ui/blob/main/packages/components/src/progress/circular-progress.tsx

    TODO: Consider extracting this logic into our own "CircularProgress"
    component if we need to reuse.
*/
const GoalIcon = forwardRef(
    (
        { goal, size = 'lg', showProgress = false, ...props }: GoalIconProps,
        ref: ForwardedRef<HTMLDivElement>
    ) => {
        const { userInfo } = useCurrentUser();
        const { currentRateInfo } = useCurrentRateInfo();

        const config = configByGoalType[goal.type];
        const sizeProps = getGoalIconSizeProps(size);
        const sizePx = toPx(sizeProps.size);

        if (!currentRateInfo)
            return <Center w={sizePx} h={sizePx} {...props} />;

        const progress = showProgress
            ? config.getProgress({
                  goal,
                  userInfo,
                  currentRateInfo,
              })
            : null;

        return (
            <GoalIconBase
                type={goal.type}
                progress={progress}
                size={size}
                ref={ref}
                {...props}
            />
        );
    }
);

interface SVGCircleProps extends HTMLChakraProps<'circle'> {}

const SVGCircle = (props: SVGCircleProps) => (
    <chakra.circle fill="transparent" {...props} />
);

export const GoalIconBase = forwardRef(
    (
        {
            type,
            progress,
            size = 'lg',
            inverted,
            filled,
            ...props
        }: {
            type: GoalType;
            progress: number | null;
            size?: GoalIconSize;
            inverted?: boolean;
            filled?: boolean;
        } & CenterProps,
        ref
    ) => {
        const config = configByGoalType[type];
        const params = getGoalIconBaseParams({
            type,
            progress,
            size,
            inverted,
            filled,
        });
        const sizePx = toPx(params.sizePx);
        const rPx = toPx(params.rPx);
        const strokeWidthPx = toPx(params.strokeWidthPx);

        return (
            <Center pos="relative" w={sizePx} h={sizePx} ref={ref} {...props}>
                <chakra.svg
                    viewBox={`0 0 ${GOAL_ICON_VIEWBOX} ${GOAL_ICON_VIEWBOX}`}
                    __css={{
                        width: sizePx,
                        height: sizePx,
                    }}
                >
                    <SVGCircle
                        cx={GOAL_ICON_VIEWBOX / 2}
                        cy={GOAL_ICON_VIEWBOX / 2}
                        r={rPx}
                        pos="absolute"
                        stroke={'gray.200'}
                        strokeWidth={strokeWidthPx}
                    />
                    <SVGCircle
                        cx={GOAL_ICON_VIEWBOX / 2}
                        cy={GOAL_ICON_VIEWBOX / 2}
                        r={rPx}
                        pos="absolute"
                        stroke={params.stroke}
                        strokeWidth={strokeWidthPx}
                        strokeDashoffset={60}
                        strokeDasharray={params.strokeDasharray}
                        transitionProperty="stroke-dasharray, stroke"
                        transitionDuration="0.6s"
                        transitionTimingFunction="ease"
                        fill={params.fill}
                    />
                </chakra.svg>
                <Icon
                    name={config.iconName}
                    pos="absolute"
                    size={params.iconSize}
                    color={params.iconColor}
                />
            </Center>
        );
    }
);

export default GoalIcon;
